import { useEffect, useState } from 'react'
import { useLoaderData } from 'react-router-dom'

import { Analytics } from 'analytics/analytics.events'
import {
  moveContainer,
  updateOrderStatus
} from 'client/stock-transfers/stock-transfers.client'
import { useRequiredParams } from 'hooks/params.hook'
import {
  OrderDetailSchema,
  type TransferPalletData
} from 'types/assembly/model'
import { ZOD_ERROR } from 'utils/constants'
import { toastError, toastSuccess } from 'utils/toast'
import { useGoToAssembly } from '../assembly.navigator'

export const useTransferPage = () => {
  const { transferId, docNum } = useRequiredParams('transferId', 'docNum')
  const [scannedPallet, setScannedPallet] = useState<string | null>(null)
  const [totalSKUs, setTotalSKUs] = useState(0)
  const [loading, setLoading] = useState(false)
  const [warehouse, setWarehouse] = useState('')
  const [palletsData, setPalletsData] = useState<
    Record<string, TransferPalletData>
  >({})
  const assemblyPage = useGoToAssembly()
  const rawOrder = useLoaderData()

  useEffect(() => {
    const result = OrderDetailSchema.safeParse(rawOrder)
    if (!result.success) {
      console.info(result.error)
      throw Error(ZOD_ERROR)
    }

    const palletsMap: Record<string, TransferPalletData> = {}
    let skuCounter = 0
    result.data.containers.forEach((container, index) => {
      skuCounter += container.totalSKUs
      palletsMap[container.id] = {
        container: container.id,
        channel: 'AB-EMB-C1',
        currentPallet: index === 0
      }
    })
    setTotalSKUs(skuCounter)
    setPalletsData(palletsMap)
    setWarehouse(result.data.toWarehouse)
  }, [rawOrder, totalSKUs])

  const onGoBack = () => {
    assemblyPage.go()
  }

  const getNextPalletKey = (pallets: Record<string, TransferPalletData>) => {
    return Object.keys(pallets).find((key) => !pallets[key].palletReady) ?? null
  }

  const markPalletAsScanned = (
    scanned: string,
    palletsData: Record<string, TransferPalletData>
  ) => {
    const palletData = palletsData[scanned] ?? null
    if (!palletData) {
      toastError('No se encontró la tarima escaneada')
      return null
    } else if (palletData.palletReady) {
      toastError('La tarima ya ha sido escaneada')
      return null
    }
    return {
      ...palletsData,
      [scanned]: { ...palletData, palletScanned: true }
    }
  }

  const markPalletAsReady = async (
    palletsData: Record<string, TransferPalletData>,
    scannedPallet: string
  ) => {
    setLoading(true)
    const palletData = palletsData[scannedPallet]

    try {
      await moveContainer(transferId, scannedPallet, palletData.channel)
    } catch (error) {
      console.error('Error moving the container:', error)
    }

    const updatedPalletData = {
      ...palletsData,
      [scannedPallet]: {
        ...palletsData[scannedPallet],
        palletReady: true,
        currentPallet: false
      }
    }

    setLoading(false)
    return updatedPalletData
  }

  const updateNextCurrentPallet = (
    palletsData: Record<string, TransferPalletData>
  ) => {
    const nextPalletKey = getNextPalletKey(palletsData)
    if (nextPalletKey) {
      return {
        ...palletsData,
        [nextPalletKey]: { ...palletsData[nextPalletKey], currentPallet: true }
      }
    }
    return palletsData
  }

  const isLastPallet = (
    pallets: Record<string, TransferPalletData>,
    currentKey: string
  ) => {
    const updatedPallets = {
      ...pallets,
      [currentKey]: { ...pallets[currentKey], palletReady: true }
    }
    return Object.values(updatedPallets).every((pallet) => pallet.palletReady)
  }

  const onScan = async (scanned: string) => {
    if (!scannedPallet) {
      const updatedPallets = markPalletAsScanned(scanned, palletsData)
      if (!updatedPallets) {
        return
      }

      setScannedPallet(scanned)
      setPalletsData(updatedPallets)
      return
    }

    if (palletsData[scannedPallet]?.channel === scanned) {
      const updatedPallets = await markPalletAsReady(palletsData, scannedPallet)
      const finalPallets = updateNextCurrentPallet(updatedPallets)
      setPalletsData(finalPallets)
      setScannedPallet(null)

      if (isLastPallet(palletsData, scannedPallet)) {
        try {
          setLoading(true)
          const result = await updateOrderStatus(transferId, 'inLoading')
          Analytics.assembly.updateOrderStatus({
            transferId,
            docNum,
            statusFromBackend: result?.orderStatus || 'error',
            fromScreen: 'transfer'
          })
          toastSuccess(`Haz finalizado el armado de la solicitud ${docNum}.`)
          assemblyPage.go()
        } catch (error) {
          console.error(error)
        } finally {
          setLoading(false)
        }
      }

      return
    }

    toastError('El canal escaneado no coincide con la tarima')
  }

  return {
    docNum,
    palletsData,
    loading,
    totalSKUs,
    warehouse,
    actions: { onGoBack, onScan }
  }
}
